import { useEffect, useMemo, useState } from 'react';
import { AdminRelay } from '../components/AdminRelay.tsx';
import { Link } from '../components/Link.tsx';
import { useCtx } from '../lib.ts';

interface WallCam {
  cameraId: string;
  name: string | null;
  city: string | null;
  countryCode: string | null;
  country: string | null;
  lagMs: number | null;
  onAir: boolean;
  planned: number | null;
}

const flag = (cc: string | null) => (cc && /^[A-Z]{2}$/.test(cc) ? String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : '🌐');

/**
 * Video wall: every camera our live counters are watching right now — the one on air large, the planned ones
 * around it — each with its counting line, track boxes and "+1". The control-room screen.
 */
export function Wall() {
  const { backend } = useCtx();
  const server = backend.server;
  const [cams, setCams] = useState<WallCam[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [focus, setFocus] = useState<string | null>(null);
  useEffect(() => {
    if (!server) return;
    const load = () =>
      void server.get<{ cameras: WallCam[] }>('/v1/admin/live/wall').then((r) => {
        if (r.ok) {
          setCams(r.value.cameras);
          setError(null);
        } else setError(r.error);
      });
    const first = setTimeout(load, 0);
    const t = setInterval(load, 10_000);
    return () => (clearTimeout(first), clearInterval(t));
  }, [server]);
  const post = useMemo(() => server?.post.bind(server), [server]);
  // Stable tile order: on air first, then planned slots (a camera keeps its tile while it stays warm).
  const ordered = useMemo(() => (cams ?? []).slice().sort((a, b) => Number(b.onAir) - Number(a.onAir) || (a.planned ?? 9) - (b.planned ?? 9) || a.cameraId.localeCompare(b.cameraId)), [cams]);
  if (!server || !post) return <p className="empty">The video wall needs the UrbanRats backend.</p>;
  if (!cams) return <p className="empty">{error ?? 'Loading the wall…'}</p>;
  const big = focus ? ordered.find((c) => c.cameraId === focus) : ordered[0];
  const rest = ordered.filter((c) => c !== big);
  return (
    <div className="wall" data-testid="video-wall">
      <div className="cc-head">
        <div>
          <h1>Video wall</h1>
          <p className="note">Every camera a live counter is watching now ({cams.length}). Boxes are the counter’s tracks; “+1” is a counted crossing. Click a tile to make it large.</p>
        </div>
        <div className="cc-head-tags">
          <Link to="/live" className="pill">
            Live operations →
          </Link>
        </div>
      </div>
      {cams.length === 0 ? (
        <p className="empty">No live counter is running (the camera ingest or the live game is stopped).</p>
      ) : (
        <div className={`wall-grid c${Math.min(ordered.length, 3)}`}>
          {big && <Tile key={big.cameraId} c={big} big post={post} apiBase={server.apiBase} count={counts[big.cameraId]} onCount={(n) => setCounts((m) => (m[big.cameraId] === n ? m : { ...m, [big.cameraId]: n }))} />}
          {rest.map((c) => (
            <Tile key={c.cameraId} c={c} post={post} apiBase={server.apiBase} count={counts[c.cameraId]} onPick={() => setFocus(c.cameraId)} onCount={(n) => setCounts((m) => (m[c.cameraId] === n ? m : { ...m, [c.cameraId]: n }))} />
          ))}
        </div>
      )}
    </div>
  );
}

function Tile({ c, big = false, post, apiBase, count, onPick, onCount }: { c: WallCam; big?: boolean; post: <T>(path: string, body: Record<string, unknown>) => Promise<{ ok: true; value: T } | { ok: false; error: string }>; apiBase: string; count: number | undefined; onPick?: () => void; onCount: (n: number) => void }) {
  return (
    <section className={`wall-tile ${big ? 'big' : ''} ${c.onAir ? 'on-air' : c.planned ? 'planned' : ''}`} onClick={onPick} role={onPick ? 'button' : undefined} tabIndex={onPick ? 0 : undefined} aria-label={onPick ? `Show ${c.city ?? c.cameraId} large` : undefined}>
      <div className="wall-media">
        <AdminRelay cameraId={c.cameraId} apiBase={apiBase} post={post} compact onPicture={(p) => onCount(p.count)} />
      </div>
      <div className="wall-cap">
        <span className="flag" aria-hidden>
          {flag(c.countryCode)}
        </span>
        <span className="where">
          <strong>{c.city ?? '—'}</strong>
          <span className="muted">{c.name ?? c.cameraId}</span>
        </span>
        <span className="wall-tags">
          {c.onAir && <span className="tag live-tag-x">● ON AIR</span>}
          {!c.onAir && c.planned && <span className="tag st-PENDING">next #{c.planned}</span>}
          <span className="wall-count" title="crossings counted since the tile opened">
            {count ?? 0}
          </span>
        </span>
      </div>
    </section>
  );
}
